import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { FilterClause } from '../api/client';
import { useEngine, useWidgets } from '../api/context';
import { useApiQuery, useBulk, useMeta, useRemove, useResourceList, useUpdate } from '../api/hooks';
import { can, fieldOf, type FieldMeta, type ResourceMeta, type ViewKind } from '../meta/types';
import { openExternal, systemName, useDataSource } from '../meta/useDataSource';
import { Button } from '../design/primitives/Button';
import { EmptyState, ErrorBanner, PageHeader, Skeleton } from '../design/primitives/Misc';
import { DropdownMenu, useConfirm, type MenuItemDef } from '../design/primitives/Overlay';
import { Tabs } from '../design/primitives/Tabs';
import { Dialog } from '../design/primitives/Overlay';
import { Field, Input, Switch } from '../design/primitives/Input';
import { useToast } from '../design/primitives/Toast';
import type { IconName } from '../design/icons';
import { useShell } from '../shell/ShellProvider';
import { DataGrid, type BulkActionDef, type ColumnState } from '../views/DataGrid';
import { BulkEditDialog } from './BulkEditDialog';
import { EnumChips, FilterChips, SearchBox } from '../views/FilterBar';
import { Board } from '../views/Board';
import { Timeline, type TimelineItem } from '../views/Timeline';
import { Calendar, type CalendarEvent } from '../views/Calendar';
import { Mailbox } from '../views/Mailbox';
import { useInspector } from '../views/Inspector';
import { columnsFromMeta } from './columns';
import { useListState } from './useListState';
import { useSavedViews } from './savedViews';
import { ActionButtons, resolveActions, useActionRunner } from './actions';
import { ResourceFormScreen, labelOf } from './ResourceFormScreen';
import { usePageActions } from '../layout/shortcuts';
import { renderField } from '../fields/registry';
import type { ScreenDef } from './types';
import { getPath } from '../meta/path';

// ResourceListScreen: title + toolbar (search, chip filters, advanced filter chips, view
// switcher, saved views, New) + the active view (table/board/timeline/calendar) + bulk bar
// + the create/edit drawer. Everything is driven by ScreenDef + ResourceMeta.

export interface ResourceListScreenProps {
  def: ScreenDef;
  /** Embedded (related list): local state, no page header, extra locked filters. */
  embedded?: boolean;
  lockedFilters?: FilterClause[];
  title?: ReactNode;
  /** Values pre-filled on "New" (e.g. the parent id on a related list). */
  createInitial?: Record<string, unknown>;
}

const VIEW_ICON: Record<ViewKind, IconName> = { table: 'table', board: 'kanban', timeline: 'gantt-chart', calendar: 'calendar', mailbox: 'inbox' };

/** The view a person last chose for a resource's list (a per-browser convenience). */
const VIEW_PREF = (resource: string) => `hf.view.${resource}`;
function readViewPref(resource: string, allowed: ViewKind[] | undefined): ViewKind | undefined {
  try {
    const v = localStorage.getItem(VIEW_PREF(resource)) as ViewKind | null;
    return v && (allowed ?? ['table']).includes(v) ? v : undefined;
  } catch {
    return undefined;
  }
}
function writeViewPref(resource: string, v: ViewKind): void {
  try {
    localStorage.setItem(VIEW_PREF(resource), v);
  } catch {
    /* storage unavailable: the URL still carries the view */
  }
}

const NO_FILTERS: FilterClause[] = [];

export function ResourceListScreen({ def, embedded, lockedFilters = NO_FILTERS, title, createInitial }: ResourceListScreenProps) {
  const navigate = useNavigate();
  const { modules } = useEngine();
  const widgets = useWidgets();
  const meta = useMeta(def.resource);
  // On a connected system (Halo), the page of its own UI that manages this resource, if known.
  const { overlay } = useDataSource();
  const appLink = overlay?.app?.links[def.resource];
  const m = meta.data;
  const [initialView] = useState<ViewKind>(() => (embedded ? undefined : readViewPref(def.resource, def.views)) ?? def.views?.[0] ?? 'table');
  // The default order, kept to fields this backend can sort (meta marks the rest unsortable — a
  // connected system such as Halo refuses some), so a screen's declared default never fails the list.
  const declaredSort = def.query?.sort ? (Array.isArray(def.query.sort) ? def.query.sort.join(',') : def.query.sort) : undefined;
  const defaultSort = useMemo(() => {
    const keep = (sort: string | undefined) => {
      if (!sort || !m) return sort;
      const ok = sort.split(',').filter((s) => m.fields.find((f) => f.name === s.replace(/^-/, ''))?.sortable !== false);
      return ok.length ? ok.join(',') : undefined;
    };
    return keep(declaredSort) ?? keep(m?.defaultSort);
  }, [declaredSort, m]);
  const state = useListState({ sort: defaultSort, view: initialView }, { local: embedded });
  // Mailbox: the open record rides in the URL (`?open=<id>`) so a reading-pane view is a link.
  const [urlParams, setUrlParams] = useSearchParams();
  const openId = urlParams.get('open');
  const setOpenId = useCallback(
    (id: string | null) =>
      setUrlParams((p) => {
        const n = new URLSearchParams(p);
        if (id) n.set('open', id);
        else n.delete('open');
        return n;
      }, { replace: true }),
    [setUrlParams],
  );
  const inspector = useInspector();
  const confirm = useConfirm();
  const toast = useToast();
  const shell = useShell();
  const searchRef = useRef<HTMLInputElement>(null);
  const [colState, setColState] = useState<ColumnState>({});
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkEditIds, setBulkEditIds] = useState<string[] | null>(null);

  const baseFilters = useMemo(() => [...(def.query?.filter ?? []), ...lockedFilters], [def.query?.filter, lockedFilters]);
  const query = useMemo(
    () => ({ ...state.query, filter: [...baseFilters, ...(state.query.filter ?? [])].length ? [...baseFilters, ...(state.query.filter ?? [])] : undefined, expand: def.query?.expand, fields: def.query?.fields }),
    [state.query, baseFilters, def.query?.expand, def.query?.fields],
  );
  const list = useResourceList<Record<string, unknown>>(def.resource, query, { enabled: Boolean(m), keepPrevious: true, limit: embedded ? 25 : 50 });
  const keyOf = useCallback((r: Record<string, unknown>) => String(r[m?.idField ?? 'id']), [m?.idField]);

  const update = useUpdate(def.resource);
  const remove = useRemove(def.resource, { toast: { success: `${m?.label ?? 'Record'} deleted` } });
  const bulk = useBulk(def.resource, { toast: { success: false, error: false } });
  const runner = useActionRunner(def.resource, { onDone: () => setSelected(new Set()) });

  // Detail navigation: explicit detailPath, else a registered `${path}/:id` screen, else the inspector.
  const detailPath = useMemo(() => {
    if (def.detailPath !== undefined) return def.detailPath;
    const candidate = `${def.path.replace(/\/$/, '')}/:id`;
    const exists = modules.some((mod) => mod.screens.some((s) => s.path === candidate && s.kind === 'detail'));
    return exists ? candidate : null;
  }, [def.detailPath, def.path, modules]);

  const columns = useMemo(() => (m ? columnsFromMeta(m, def.columns) : []), [m, def.columns]);
  // One-click filter buttons: the screen's declared (non-chip) filters first — Status, Priority,
  // Queue, Assignee… — then the list's other visible pickable columns, in order.
  const quickFilters = useMemo(() => {
    const declared = (def.filters ?? []).filter((f) => f.kind !== 'chips').map((f) => f.field);
    const hidden = new Set(colState.hidden ?? columns.filter((c) => c.hidden).map((c) => c.id));
    const chipFields = new Set((def.filters ?? []).filter((f) => f.kind === 'chips').map((f) => f.field));
    const fromColumns = columns
      .filter((c) => c.field && !hidden.has(c.id) && !chipFields.has(c.field.name) && c.field.filterable && ['enum', 'relation', 'boolean'].includes(c.field.type))
      .map((c) => c.field!.name);
    return [...new Set([...declared, ...fromColumns])].slice(0, 6);
  }, [columns, colState.hidden, def.filters]);
  const chipFilters = useMemo(() => (def.filters ?? []).filter((f) => f.kind === 'chips' || (!f.kind && fieldOf(m ?? ({ fields: [] } as unknown as ResourceMeta), f.field)?.type === 'enum')), [def.filters, m]);
  const lockedNames = useMemo(() => [...baseFilters.map((f) => f.field), ...chipFilters.map((f) => f.field)], [baseFilters, chipFilters]);

  const counts = useMemo(() => {
    const out: Record<string, Record<string, number>> = {};
    for (const cf of chipFilters) {
      const c: Record<string, number> = {};
      for (const r of list.rows) {
        const v = r[cf.field];
        if (v != null) c[String(v)] = (c[String(v)] ?? 0) + 1;
      }
      out[cf.field] = c;
    }
    return out;
  }, [chipFilters, list.rows]);

  const saved = useSavedViews(def.resource);
  // The connected system's own views (e.g. Halo's filter profiles and team tree with counts), when
  // the resource offers them (action `source-views`) and the tenant runs on that system.
  const offersSourceViews = Boolean(!embedded && overlay && m?.actions.some((a) => a.name === 'source-views'));
  const sourceViews = useApiQuery<{ data: SourceView[] }>(['source-views', def.resource], `/v1/${def.resource}/source-views`, { enabled: offersSourceViews, staleTime: 60_000 });
  const activeSourceView = (sourceViews.data?.data ?? []).find((v) => (v.source_view ? v.source_view === state.sourceView : !state.sourceView && v.filters.length > 0 && sameFilters(v.filters, state.filters)));
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saveShared, setSaveShared] = useState(false);

  const openRow = useCallback(
    (row: Record<string, unknown>) => {
      const id = keyOf(row);
      if (def.inspector || (!detailPath && m)) {
        inspector.show({
          key: `${def.resource}:${id}`,
          title: labelOf(m, row),
          subtitle: m?.label,
          content: <InspectorOverview meta={m!} row={row} />,
          actions: m?.capabilities.update ? (
            <Button size="sm" variant="ghost" icon="pencil" onClick={() => state.openEdit(id)}>
              Edit
            </Button>
          ) : undefined,
        });
        return;
      }
      if (detailPath) navigate(detailPath.replace(':id', id));
    },
    [def.inspector, def.resource, detailPath, inspector, keyOf, m, navigate, state],
  );

  // The page's actions, registered once (usePageActions): / search, n new, r refresh, and every
  // collection action — reachable by key, from ⌘K (`>`), the `.` quick menu and the `?` sheet.
  const pageCollection = m ? resolveActions(m, def.actions, 'collection').filter((a) => !a.component) : [];
  usePageActions(
    embedded || !m
      ? []
      : [
          ...(m.searchFields.length ? [{ id: 'search', label: `Search ${m.labelPlural.toLowerCase()}`, keys: '/', icon: 'search' as IconName, run: () => searchRef.current?.focus() }] : []),
          ...(def.create !== false && m.capabilities.create && can(m, 'create') ? [{ id: 'new', label: `New ${m.label.toLowerCase()}`, keys: 'n', icon: 'plus' as IconName, run: () => state.openNew() }] : []),
          { id: 'refresh', label: 'Refresh', keys: 'r', icon: 'refresh-cw' as IconName, run: () => void list.refetch() },
          ...pageCollection.map((a) => ({ id: `action:${a.name}`, label: a.label, icon: a.icon as IconName | undefined, run: () => runner.run(a, { refresh: list.refetch }) })),
        ],
  );

  useEffect(() => setSelected(new Set()), [state.q, state.filters]);

  if (meta.error) return <ErrorBanner error={meta.error} />;
  if (!m) return <Skeleton lines={6} />;

  const canCreate = def.create !== false && m.capabilities.create && can(m, 'create');
  const canUpdate = m.capabilities.update && can(m, 'update');
  const canRemove = m.capabilities.remove && can(m, 'delete');
  const editableFields = m.fields.filter((f) => f.inUpdate && !f.readOnly);
  const collectionActions = resolveActions(m, def.actions, 'collection');
  const itemActions = resolveActions(m, def.actions, 'item');
  const noun = (n: number) => (n === 1 ? m.label.toLowerCase() : m.labelPlural.toLowerCase());

  // Bulk delete over the /bulk endpoint (op:'delete') — one transaction, per-id savepoints.
  const bulkDelete = async (ids: string[]) => {
    if ((await confirm({ title: `Delete ${ids.length} ${noun(ids.length)}?`, body: 'This cannot be undone.', confirmLabel: 'Delete', danger: true })) === false) return;
    const res = await bulk.mutateAsync({ op: 'delete', ids });
    if (res.failed) toast({ kind: 'error', title: `${res.failed} of ${ids.length} could not be deleted`, detail: res.results.find((r) => !r.ok)?.error?.message });
    else toast({ title: `${res.succeeded} ${noun(res.succeeded)} deleted` });
    setSelected(new Set());
    void list.refetch();
  };

  const bulkActions: BulkActionDef[] = [
    ...(canUpdate && editableFields.length ? [{ name: 'bulk-edit', label: 'Edit fields', icon: 'pencil' as IconName, run: (ids: string[]) => setBulkEditIds(ids) }] : []),
    ...itemActions.filter((a) => !a.component).map((a) => ({ name: a.name, label: a.label, icon: a.icon as IconName | undefined, tone: a.tone, run: (ids: string[]) => runner.run(a, { selected: ids }) })),
    ...(canRemove ? [{ name: 'remove', label: 'Delete', icon: 'trash' as IconName, tone: 'danger' as const, run: bulkDelete }] : []),
  ];

  // The right-click / "⋯" / Menu-key row menu. Multi-selection shows the bulk actions; a
  // single row shows Open / Open-in-new-window (desktop skin) / Copy link / its item actions.
  const rowMenu = (row: Record<string, unknown>, ids: string[]): MenuItemDef[] => {
    const id = keyOf(row);
    if (ids.length > 1) {
      return bulkActions.map((a) => ({ key: a.name, label: `${a.label} (${ids.length})`, icon: a.icon, danger: a.tone === 'danger', onSelect: () => void a.run(ids) }));
    }
    const detail = detailPath ? detailPath.replace(':id', id) : null;
    const items: MenuItemDef[] = [];
    if (detail || def.inspector) items.push({ key: 'open', label: 'Open', icon: 'external-link', onSelect: () => openRow(row) });
    if (detail && shell.openWindow) items.push({ key: 'open-window', label: 'Open in new window', icon: 'copy', onSelect: () => shell.openWindow!(detail, labelOf(m, row)) });
    if (detail) items.push({ key: 'copy', label: 'Copy link', icon: 'link', onSelect: () => copyLink(detail) });
    const acts = resolveActions(m, def.actions, 'item', row).filter((a) => !a.component);
    if (acts.length && items.length) items.push({ separator: true, label: '' });
    for (const a of acts) items.push({ key: a.name, label: a.label, icon: a.icon as IconName | undefined, danger: a.tone === 'danger', onSelect: () => runner.run(a, { id, row }) });
    if (canUpdate) {
      if (items.length) items.push({ separator: true, label: '' });
      items.push({ key: 'edit', label: 'Edit', icon: 'pencil', hint: 'E', onSelect: () => state.openEdit(id) });
    }
    if (canRemove) items.push({ key: 'delete', label: 'Delete', icon: 'trash', danger: true, onSelect: () => void deleteOne(row) });
    return items;
  };

  const deleteOne = async (row: Record<string, unknown>) => {
    if ((await confirm({ title: `Delete ${labelOf(m, row)}?`, body: 'This cannot be undone.', confirmLabel: 'Delete', danger: true })) !== false) await remove.mutateAsync(keyOf(row));
  };

  const copyLink = (path: string) => {
    try {
      const href = new URL(path, window.location.origin).href;
      void navigator.clipboard?.writeText(href);
      toast({ title: 'Link copied' });
    } catch {
      toast({ kind: 'error', title: 'Could not copy link' });
    }
  };

  const viewTabs = (def.views ?? ['table']).map((v) => ({ key: v, label: v[0]!.toUpperCase() + v.slice(1), icon: VIEW_ICON[v] }));
  const view: ViewKind = (def.views ?? ['table']).includes(state.view) ? state.view : (def.views?.[0] ?? 'table');

  const savedMenu: MenuItemDef[] = [
    ...(saved.views.length ? [{ heading: true, label: 'Saved views' }] : []),
    ...saved.views.map((v) => ({
      key: v.id,
      label: v.name,
      hint: v.shared ? 'shared' : undefined,
      checked: state.savedView === v.id,
      onSelect: () => {
        state.apply({ filters: v.query.filters ?? [], where: v.query.where ?? null, sort: v.query.sort ?? [], q: v.query.q ?? '', view: v.view_type, groupBy: v.query.groupBy ?? null, sourceView: null });
        state.setSavedView(v.id);
        if (v.columns) setColState(v.columns);
      },
    })),
    ...(saved.views.length ? [{ separator: true, label: '' }] : []),
    ...sourceViewItems(sourceViews.data?.data ?? [], activeSourceView?.id ?? null, overlay ? systemName(overlay) : '', (v) =>
      state.apply({ filters: v.filters, where: null, q: '', sourceView: v.source_view, savedView: null }),
    ),
    { label: 'Save current view…', icon: 'save' as IconName, onSelect: () => setSaveOpen(true) },
    ...(state.savedView ? [{ label: 'Delete this view', icon: 'trash' as IconName, danger: true, onSelect: () => saved.remove.mutate(state.savedView!) }] : []),
    { label: 'Reset filters', icon: 'rotate-ccw' as IconName, onSelect: () => state.reset() },
  ];

  const emptyState = state.q || state.filters.length || state.where ? (
    <EmptyState title={`No matching ${m.labelPlural.toLowerCase()}`} hint="Try clearing the search or filters." icon="search" action={<Button variant="ghost" onClick={() => state.reset()}>Clear filters</Button>} />
  ) : (
    <EmptyState title={`No ${m.labelPlural.toLowerCase()} yet`} hint={canCreate ? `Create the first ${m.label.toLowerCase()} to get started.` : undefined} icon={(m.icon as IconName) ?? 'inbox'} action={canCreate ? <Button variant="primary" icon="plus" onClick={() => state.openNew()}>New {m.label.toLowerCase()}</Button> : undefined} />
  );

  const cellEdit = async (row: Record<string, unknown>, field: FieldMeta, value: unknown, previous: unknown) => {
    await update.mutateAsync({ id: keyOf(row), body: { [field.name]: value }, previous: { [field.name]: previous } });
  };

  const toolbarLeft = (
    <>
      {m.searchFields.length > 0 && <SearchBox value={state.q} onChange={state.setQ} placeholder={`Search ${m.labelPlural.toLowerCase()}…`} inputRef={searchRef} aria-label={`Search ${m.labelPlural.toLowerCase()}`} />}
      {chipFilters.map((cf) => {
        const f = fieldOf(m, cf.field);
        if (!f) return null;
        const cur = state.filters.find((x) => x.field === cf.field && x.op === 'eq');
        return (
          <EnumChips
            key={cf.field}
            field={f}
            label={cf.label ?? `Filter by ${f.label.toLowerCase()}`}
            value={cur ? String(cur.value) : null}
            counts={cf.counts === false ? undefined : counts[cf.field]}
            onChange={(v) => state.setFilters([...state.filters.filter((x) => x.field !== cf.field), ...(v ? [{ field: cf.field, op: 'eq' as const, value: v }] : [])])}
          />
        );
      })}
      <FilterChips meta={m} filters={state.filters} onChange={state.setFilters} locked={lockedNames} quick={quickFilters} where={state.where} onWhereChange={state.setWhere} />
    </>
  );

  const tools = (
    <>
      {viewTabs.length > 1 && (
        <Tabs
          variant="pills"
          tabs={viewTabs}
          value={view}
          onChange={(k) => {
            state.setView(k as ViewKind);
            if (!embedded) writeViewPref(def.resource, k as ViewKind);
          }}
          aria-label="View"
        />
      )}
      {!embedded && <DropdownMenu aria-label="Saved views" trigger={<Button size="sm" variant="ghost" icon="bookmark" iconRight="chevron-down">{saved.views.find((v) => v.id === state.savedView)?.name ?? activeSourceView?.name ?? 'Views'}</Button>} items={savedMenu} />}
    </>
  );

  let body: ReactNode;
  if (view === 'board' && def.board) {
    const f = fieldOf(m, def.board.groupBy);
    body = f ? (
      <Board
        rows={list.rows}
        keyOf={keyOf}
        field={f}
        labelOf={(r) => labelOf(m, r)}
        cardFields={(def.board.cardFields ?? []).map((n) => fieldOf(m, n)).filter((x): x is FieldMeta => Boolean(x))}
        widgets={widgets}
        wip={def.board.wip}
        onOpen={openRow}
        onMove={(row, value, previous) => update.mutate({ id: keyOf(row), body: { [f.name]: value }, previous: { [f.name]: previous } })}
        empty={emptyState}
      />
    ) : (
      <ErrorBanner error={`Board field ${def.board.groupBy} not found`} />
    );
  } else if (view === 'timeline' && def.timeline) {
    const t = def.timeline;
    const items: TimelineItem[] = list.rows.map((r) => ({
      id: keyOf(r),
      label: labelOf(m, r),
      start: (r[t.startField] as string | null) ?? null,
      end: (r[t.endField] as string | null) ?? (r[t.startField] as string | null) ?? null,
      progress: t.progressField ? Number(r[t.progressField] ?? 0) : undefined,
      dependsOn: t.dependsField ? ((r[t.dependsField] as string[] | null) ?? []) : undefined,
      group: t.groupField ? String(r[t.groupField] ?? '') : undefined,
      tone: statusTone(m, r),
    }));
    body = <Timeline items={items} onOpen={(id) => { const r = list.rows.find((x) => keyOf(x) === id); if (r) openRow(r); }} onChange={(id, start, end) => { const r = list.rows.find((x) => keyOf(x) === id); update.mutate({ id, body: { [t.startField]: start, [t.endField]: end }, previous: r ? { [t.startField]: r[t.startField], [t.endField]: r[t.endField] } : undefined }); }} empty={emptyState} />;
  } else if (view === 'mailbox' && def.mailbox) {
    const mb = def.mailbox;
    const Reading = mb.reading;
    const secondaryField = mb.secondaryField ? fieldOf(m, mb.secondaryField) : undefined;
    const dateDesc = (state.sort[0]?.id ?? mb.dateField) === mb.dateField ? state.sort[0]?.desc !== false : true;
    body = (
      <Mailbox
        label={m.labelPlural}
        rows={list.rows.map((r) => ({
          id: keyOf(r),
          label: labelOf(m, r),
          secondary: secondaryField ? renderField(secondaryField, getPath(r, secondaryField.name), r, { compact: true, widgets }) : undefined,
          date: (r[mb.dateField] as string | null) ?? null,
        }))}
        selectedId={openId}
        onSelect={setOpenId}
        reading={(id) => {
          if (Reading) return <Reading id={id} />;
          const row = list.rows.find((r) => keyOf(r) === id);
          return row ? <InspectorOverview meta={m} row={row} /> : null;
        }}
        toolbar={
          <Button size="sm" variant="ghost" icon={dateDesc ? 'arrow-down' : 'arrow-up'} onClick={() => state.setSort([{ id: mb.dateField, desc: !dateDesc }])} aria-label={dateDesc ? 'Newest first — show oldest first' : 'Oldest first — show newest first'}>
            {dateDesc ? 'Newest first' : 'Oldest first'}
          </Button>
        }
        empty={emptyState}
        hasMore={list.hasMore}
        loadingMore={list.loadingMore}
        loadMore={list.loadMore}
      />
    );
  } else if (view === 'calendar' && def.calendar) {
    const c = def.calendar;
    const events: CalendarEvent[] = list.rows.filter((r) => r[c.dateField]).map((r) => ({ id: keyOf(r), label: labelOf(m, r), start: String(r[c.dateField]), end: c.endField ? (r[c.endField] as string | null) : null, tone: statusTone(m, r) }));
    body = <Calendar events={events} onOpen={(id) => { const r = list.rows.find((x) => keyOf(x) === id); if (r) openRow(r); }} onCreate={canCreate ? () => state.openNew() : undefined} />;
  } else {
    body = (
      <DataGrid
        aria-label={m.labelPlural}
        meta={m}
        columns={columns}
        rows={list.rows}
        keyOf={keyOf}
        loading={list.isLoading}
        refreshing={list.isFetching && !list.isLoading && !list.loadingMore}
        error={list.error}
        empty={emptyState}
        hasMore={list.hasMore}
        loadMore={list.loadMore}
        loadingMore={list.loadingMore}
        sort={state.sort}
        onSortChange={state.setSort}
        onFilterAdd={state.addFilter}
        selection={bulkActions.length ? selected : undefined}
        onSelectionChange={bulkActions.length ? setSelected : undefined}
        onRowClick={detailPath || def.inspector ? openRow : undefined}
        onCellEdit={m.capabilities.update && can(m, 'update') ? cellEdit : undefined}
        groupBy={state.groupBy}
        onGroupByChange={state.setGroupBy}
        columnState={colState}
        onColumnStateChange={setColState}
        bulkActions={bulkActions}
        rowMenu={rowMenu}
        exportName={m.labelPlural.toLowerCase()}
        widgets={widgets}
        toolbarLeft={toolbarLeft}
        tools={tools}
        height={embedded ? 320 : undefined}
      />
    );
  }

  return (
    <div className={embedded ? 'hf-embedded' : 'hf-page'}>
      {!embedded && (
        <PageHeader
          title={title ?? def.title ?? m.labelPlural}
          subtitle={def.subtitle}
          eyebrow={m.module}
          actions={
            <>
              <ActionButtons actions={collectionActions} onRun={runner.run} ctx={{ refresh: list.refetch }} max={1} />
              {overlay && appLink && (
                <Button icon="external-link" onClick={() => openExternal(appLink)}>
                  {`Open in ${systemName(overlay)}`}
                </Button>
              )}
              {canCreate && (
                <Button variant="primary" icon="plus" onClick={() => state.openNew()}>
                  New {m.label.toLowerCase()}
                </Button>
              )}
            </>
          }
        />
      )}
      {embedded && (
        <div className="hf-between" style={{ marginBottom: 8 }}>
          <div>{title}</div>
          <div className="hf-row">
            <ActionButtons actions={collectionActions} onRun={runner.run} ctx={{ refresh: list.refetch }} max={1} size="sm" />
            {canCreate && (
              <Button size="sm" icon="plus" onClick={() => state.openNew()}>
                New {m.label.toLowerCase()}
              </Button>
            )}
          </div>
        </div>
      )}
      {view !== 'table' && (
        <div className="hf-grid-tools">
          <div className="hf-row hf-grow" style={{ flexWrap: 'wrap' }}>
            {toolbarLeft}
          </div>
          <div className="hf-row">{tools}</div>
        </div>
      )}
      {body}
      {runner.dialogs}
      {bulkEditIds && (
        <BulkEditDialog
          resource={def.resource}
          ids={bulkEditIds}
          fields={editableFields}
          label={m.label}
          labelPlural={m.labelPlural}
          onClose={() => setBulkEditIds(null)}
          onDone={() => {
            setSelected(new Set());
            void list.refetch();
          }}
        />
      )}
      {/* `?new=1` only opens the generic create drawer when the resource can be created; a screen
          action with `openParam: 'new'` (Invite someone on members) owns the param otherwise. */}
      {((state.newOpen && canCreate) || state.editId) && (
        <ResourceFormScreen
          resource={def.resource}
          id={state.editId ?? undefined}
          form={def.form}
          mode={def.form?.mode === 'dialog' ? 'dialog' : 'drawer'}
          initial={createInitial}
          onClose={state.closeForm}
          onDone={(row) => {
            state.closeForm();
            list.refetch();
            if (!state.editId && detailPath && !embedded) navigate(detailPath.replace(':id', String(row[m.idField])));
          }}
        />
      )}
      <Dialog open={saveOpen} onClose={() => setSaveOpen(false)} title="Save view" description="Filters, sort, grouping and columns as they are now.">
        <form
          className="hf-stack"
          onSubmit={(e) => {
            e.preventDefault();
            if (!saveName.trim()) return;
            saved.save.mutate(
              { resource: def.resource, name: saveName.trim(), shared: saveShared, query: { filters: state.filters, where: state.where, sort: state.sort, q: state.q, groupBy: state.groupBy }, columns: colState, view_type: view },
              { onSuccess: (v) => { setSaveOpen(false); setSaveName(''); state.setSavedView(v.id); } },
            );
          }}
        >
          <Field label="Name" required>
            <Input value={saveName} onChange={(e) => setSaveName(e.target.value)} data-autofocus placeholder="e.g. Overdue this month" />
          </Field>
          <Switch checked={saveShared} onChange={(e) => setSaveShared(e.target.checked)} label="Share with the team" />
          <div className="hf-form-actions">
            <Button variant="ghost" onClick={() => setSaveOpen(false)}>Cancel</Button>
            <Button type="submit" variant="primary" busy={saved.save.isPending} disabled={!saveName.trim()}>Save view</Button>
          </div>
        </form>
      </Dialog>
    </div>
  );
}

function statusTone(m: ResourceMeta, r: Record<string, unknown>): TimelineItem['tone'] {
  const f = m.lifecycle ? fieldOf(m, m.lifecycle.field) : m.fields.find((x) => x.name === 'status');
  const v = f ? r[f.name] : undefined;
  const tone = f?.enum?.find((o) => o.value === v)?.tone;
  return tone === 'pos' || tone === 'warn' || tone === 'neg' || tone === 'muted' ? tone : 'accent';
}

/** Compact read-only overview used by the inspector. */
export function InspectorOverview({ meta, row }: { meta: ResourceMeta; row: Record<string, unknown> }) {
  const widgets = useWidgets();
  // The id is the URL, not a fact worth a row; tenant_id is never shown.
  const fields = meta.fields.filter((f) => f.inDetail && f.name !== meta.idField && f.name !== 'tenant_id');
  return (
    <dl className="hf-kv">
      {fields.map((f) => (
        <div key={f.name}>
          <dt>{f.label}</dt>
          <dd>{renderInspectorValue(f, row, widgets)}</dd>
        </div>
      ))}
    </dl>
  );
}

function renderInspectorValue(f: FieldMeta, row: Record<string, unknown>, widgets: ReturnType<typeof useWidgets>) {
  return renderField(f, getPath(row, f.name), row, { widgets });
}

/** A view offered by the connected system (GET /<resource>/source-views). */
export interface SourceView {
  id: string;
  name: string;
  group: string;
  /** The view this one sits under (an agent under its team), or null. */
  parent?: string | null;
  count: number | null;
  filters: FilterClause[];
  source_view: string | null;
}

const sameFilters = (a: FilterClause[], b: FilterClause[]) =>
  a.length === b.length && a.every((x) => b.some((y) => y.field === x.field && y.op === x.op && String(y.value) === String(x.value)));

/** The connected system's views as menu items, under a heading per group; a view with a parent (an agent of a team) is indented under it. */
function sourceViewItems(views: SourceView[], activeId: string | null, system: string, select: (v: SourceView) => void): MenuItemDef[] {
  if (!views.length) return [];
  const out: MenuItemDef[] = [];
  let group: string | null = null;
  for (const v of views) {
    if (v.group !== group) {
      group = v.group;
      out.push({ heading: true, label: out.length ? v.group : `${v.group} · from ${system}` });
    }
    out.push({ key: `source:${v.id}`, label: v.parent ? <span className="hf-menu-indent">{v.name}</span> : v.name, hint: v.count != null ? String(v.count) : undefined, checked: v.id === activeId, onSelect: () => select(v) });
  }
  out.push({ separator: true, label: '' });
  return out;
}
